/**
 * LBR2-D2 careers-page — branded public chrome (header + footer).
 * 2026-10-08: the company name + logo come from the tenant's settings (public
 * /company API) — no customer name is written in the code; with no logo the
 * header shows the company's initials.
 *
 * Deliberately self-contained: imports ONLY react-router `Link` + lucide icons.
 * It does NOT import AppShell / auth store / sidebar so the careers pages can
 * lift to a standalone host with only a routing change.
 */
import { Link } from 'react-router-dom';
import { UserCircle2 } from 'lucide-react';
import type { Company } from './careersPublicApi';

function initials(name: string): string {
  const w = name.split(/\s+/).filter(Boolean);
  return ((w[0]?.[0] ?? 'C') + (w[1]?.[0] ?? '')).toUpperCase();
}

export function CareersChrome({ children, company, showAccount = false }: { children: React.ReactNode; company?: Company | null; showAccount?: boolean }) {
  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-[#faf8f4]">
      <header className="border-b border-gray-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-3 px-4 py-3">
          <Link to="/careers" className="flex min-w-0 items-center gap-2">
            {company?.logoUrl ? (
              <img src={company.logoUrl} alt="" className="h-9 w-9 shrink-0 rounded-xl object-contain" />
            ) : (
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#1F5C3A] text-sm font-bold text-white shadow">
                {initials(company?.name ?? 'Careers')}
              </span>
            )}
            <span className="truncate font-display text-lg font-bold text-gray-900">{company?.name ?? 'Careers'}</span>
          </Link>
          {showAccount && (
            <Link
              to="/jobopenings/account"
              className="flex min-h-[40px] shrink-0 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold text-gray-700 hover:bg-gray-100"
            >
              <UserCircle2 className="h-4 w-4" /> My account
            </Link>
          )}
        </div>
      </header>
      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-6">{children}</main>
      <footer className="py-6 text-center text-xs text-gray-400">Powered by AURA · Ardhanex Tech</footer>
    </div>
  );
}

export default CareersChrome;
